import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { postEventBatch } from '../api/justWatch';

// Frontend-side capture of the signals `mw_impressions.csv` / `mw_sessions.csv`
// backfill data models: row impressions, clicks, session/device context, plus
// hover-dwell and simulated playback. Shown live in the Signals panel and Data
// tab, and best-effort batched to the just_watch API's /events route every
// FLUSH_INTERVAL_MS, which writes them to S3 (see api/src/just_watch/events_store.py).
// If that API is unreachable the events still show up locally — nothing here
// depends on the network call succeeding.

export interface ImpressionEvent {
  userId: string;
  titleId: string;
  titleName: string;
  surface: string;
  rowPosition: number;
  timestamp: number;
}

export type ClickEvent = ImpressionEvent;

export interface SearchEvent {
  userId: string;
  queryText: string;
  resultCount: number;
  resultTitleId: string | null;
  resultTitleName: string | null;
  timestamp: number;
}

export interface DwellEvent {
  userId: string;
  titleId: string;
  titleName: string;
  durationMs: number;
  timestamp: number;
}

export type PlaybackAction = 'play' | 'pause';

export interface PlaybackEvent {
  userId: string;
  titleId: string;
  titleName: string;
  action: PlaybackAction;
  elapsedSeconds: number;
  timestamp: number;
}

export interface MousePoint {
  x: number;
  y: number;
  t: number;
}

interface SessionInfo {
  device: string;
  network: string;
  startedAt: number;
}

interface AnalyticsState {
  session: SessionInfo;
  elapsedSeconds: number;
  impressions: ImpressionEvent[];
  clicks: ClickEvent[];
  searches: SearchEvent[];
  dwells: DwellEvent[];
  playbackEvents: PlaybackEvent[];
  titlesSeenCount: number;
  mouseTrailEnabled: boolean;
  mousePoints: MousePoint[];
  setMouseTrailEnabled: (enabled: boolean) => void;
  logImpression: (event: Omit<ImpressionEvent, 'timestamp' | 'userId'>) => void;
  logClick: (event: Omit<ClickEvent, 'timestamp' | 'userId'>) => void;
  logSearch: (event: Omit<SearchEvent, 'timestamp' | 'userId'>) => void;
  logDwell: (event: Omit<DwellEvent, 'timestamp' | 'userId'>) => void;
  logPlayback: (event: Omit<PlaybackEvent, 'timestamp' | 'userId'>) => void;
}

const AnalyticsContext = createContext<AnalyticsState | null>(null);

const MAX_FEED_LENGTH = 12;
const MAX_MOUSE_POINTS = 40;
const MOUSE_SAMPLE_MS = 60;
const FLUSH_INTERVAL_MS = 8000;
const MIN_DWELL_MS = 400;

function detectDevice(): string {
  const width = window.innerWidth;
  if (width < 600) return 'Mobile';
  if (width < 1024) return 'Tablet';
  return 'Web';
}

function detectNetwork(): string {
  const connection = (navigator as any).connection;
  if (connection?.type === 'cellular') return 'Cellular';
  if (connection?.effectiveType && ['2g', '3g'].includes(connection.effectiveType)) return 'Cellular';
  return 'Wi-Fi';
}

export function AnalyticsProvider({ children, userId }: { children: React.ReactNode; userId: string }) {
  const userIdRef = useRef(userId);
  userIdRef.current = userId;

  const [session] = useState<SessionInfo>(() => ({
    device: detectDevice(),
    network: detectNetwork(),
    startedAt: Date.now(),
  }));
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [impressions, setImpressions] = useState<ImpressionEvent[]>([]);
  const [clicks, setClicks] = useState<ClickEvent[]>([]);
  const [searches, setSearches] = useState<SearchEvent[]>([]);
  const [dwells, setDwells] = useState<DwellEvent[]>([]);
  const [playbackEvents, setPlaybackEvents] = useState<PlaybackEvent[]>([]);
  const [mouseTrailEnabled, setMouseTrailEnabled] = useState(false);
  const [mousePoints, setMousePoints] = useState<MousePoint[]>([]);
  const seenTitles = useRef(new Set<string>());
  const lastSampleAt = useRef(0);
  const pendingQueue = useRef<Record<string, unknown>[]>([]);

  useEffect(() => {
    const flush = () => {
      if (pendingQueue.current.length === 0) return;
      postEventBatch(pendingQueue.current);
      pendingQueue.current = [];
    };
    const id = setInterval(flush, FLUSH_INTERVAL_MS);
    window.addEventListener('beforeunload', flush);
    return () => {
      clearInterval(id);
      window.removeEventListener('beforeunload', flush);
      flush();
    };
  }, []);

  useEffect(() => {
    const id = setInterval(() => setElapsedSeconds(Math.floor((Date.now() - session.startedAt) / 1000)), 1000);
    return () => clearInterval(id);
  }, [session.startedAt]);

  useEffect(() => {
    if (!mouseTrailEnabled) return;
    const handleMove = (e: MouseEvent) => {
      const now = performance.now();
      if (now - lastSampleAt.current < MOUSE_SAMPLE_MS) return;
      lastSampleAt.current = now;
      const point: MousePoint = { x: e.clientX, y: e.clientY, t: Date.now() };
      setMousePoints((prev) => [...prev.slice(-(MAX_MOUSE_POINTS - 1)), point]);
    };
    window.addEventListener('mousemove', handleMove);
    return () => window.removeEventListener('mousemove', handleMove);
  }, [mouseTrailEnabled]);

  const enqueue = useCallback((type: string, event: Record<string, unknown>) => {
    pendingQueue.current.push({ type, user_id: userIdRef.current, ...event });
  }, []);

  const logImpression = useCallback(
    (event: Omit<ImpressionEvent, 'timestamp' | 'userId'>) => {
      seenTitles.current.add(event.titleId);
      const full = { ...event, userId: userIdRef.current, timestamp: Date.now() };
      setImpressions((prev) => [full, ...prev].slice(0, MAX_FEED_LENGTH));
      enqueue('impression', full);
    },
    [enqueue]
  );

  const logClick = useCallback(
    (event: Omit<ClickEvent, 'timestamp' | 'userId'>) => {
      const full = { ...event, userId: userIdRef.current, timestamp: Date.now() };
      setClicks((prev) => [full, ...prev].slice(0, MAX_FEED_LENGTH));
      enqueue('click', full);
    },
    [enqueue]
  );

  const logSearch = useCallback(
    (event: Omit<SearchEvent, 'timestamp' | 'userId'>) => {
      const full = { ...event, userId: userIdRef.current, timestamp: Date.now() };
      setSearches((prev) => [full, ...prev].slice(0, MAX_FEED_LENGTH));
      enqueue('search', full);
    },
    [enqueue]
  );

  const logDwell = useCallback(
    (event: Omit<DwellEvent, 'timestamp' | 'userId'>) => {
      if (event.durationMs < MIN_DWELL_MS) return;
      const full = { ...event, userId: userIdRef.current, timestamp: Date.now() };
      setDwells((prev) => [full, ...prev].slice(0, MAX_FEED_LENGTH));
      enqueue('dwell', full);
    },
    [enqueue]
  );

  const logPlayback = useCallback(
    (event: Omit<PlaybackEvent, 'timestamp' | 'userId'>) => {
      const full = { ...event, userId: userIdRef.current, timestamp: Date.now() };
      setPlaybackEvents((prev) => [full, ...prev].slice(0, MAX_FEED_LENGTH));
      enqueue('playback', full);
    },
    [enqueue]
  );

  const value = useMemo<AnalyticsState>(
    () => ({
      session,
      elapsedSeconds,
      impressions,
      clicks,
      searches,
      dwells,
      playbackEvents,
      titlesSeenCount: seenTitles.current.size,
      mouseTrailEnabled,
      mousePoints,
      setMouseTrailEnabled,
      logImpression,
      logClick,
      logSearch,
      logDwell,
      logPlayback,
    }),
    [
      session,
      elapsedSeconds,
      impressions,
      clicks,
      searches,
      dwells,
      playbackEvents,
      mouseTrailEnabled,
      mousePoints,
      logImpression,
      logClick,
      logSearch,
      logDwell,
      logPlayback,
    ]
  );

  return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>;
}

export function useAnalytics(): AnalyticsState {
  const ctx = useContext(AnalyticsContext);
  if (!ctx) throw new Error('useAnalytics must be used within an AnalyticsProvider');
  return ctx;
}
